import { useMemo } from "react";
import { diatonicChords, voiceDiatonicChord } from "../../theory/diatonic";
import { explainDegree, explainKey, explainNotes } from "../../theory/explain";
import { keyName, transposeKey } from "../../theory/key";
import { barTicks } from "../../sequencer/time";
import { useLive, usePosition, useProject, useServices } from "../context";

const FN_LABEL = { tonic: "T", predominant: "PD", dominant: "D" } as const;

/** Contextual explanations derived from the actual key, chords and held notes (F-LEARN-01). */
export function TheoryPanel() {
  const { controller } = useServices();
  const key = useProject((p) => p.song.key);
  const transpose = useProject((p) => p.song.transposeSemitones);
  const prog = useProject((p) => p.sequence.progression);
  const ts = useProject((p) => p.song.timeSignature);
  const held = useLive((s) => s.heldNotes);
  const pos = usePosition();
  const perfKey = transposeKey(key, transpose);
  const k = useMemo(() => explainKey(key), [key]);
  const chords = useMemo(() => diatonicChords(key), [key]);
  // Held notes are sounding pitches; explain them in the key they sound in.
  const heldKey = transpose !== 0 ? perfKey : key;
  const notes = useMemo(() => (held.length ? explainNotes(held, heldKey) : null), [held, heldKey]);
  const bar = pos === null || !prog.enabled ? null : Math.floor(pos / barTicks(ts));
  const playing = bar !== null ? prog.chords[bar] : undefined;

  return (
    <aside className="theory" aria-label="Theory context">
      <h2>Theory</h2>
      <section aria-labelledby="th-key">
        <h3 id="th-key">{k.name}</h3>
        <p>Scale: <span className="notes">{k.scaleNotes.join(" ")}</span></p>
        <p>Key signature: {k.signature}. Relative: {k.relative}.</p>
        {transpose !== 0 && <p className="callout">Transposed {transpose > 0 ? "up" : "down"} {Math.abs(transpose)} semitone{Math.abs(transpose) > 1 ? "s" : ""}: it sounds in <strong>{keyName(perfKey)}</strong>.</p>}
      </section>
      <section aria-labelledby="th-chords">
        <h3 id="th-chords">Chords in this key</h3>
        <ul className="chord-list">
          {chords.map((c) => (
            <li key={c.degree}>
              <button type="button" title={explainDegree(key, c.degree)} onClick={() => controller.previewNotes(voiceDiatonicChord(key, c.degree, { octave: 4 }))}>
                <span className="roman">{c.roman}</span> <span>{c.symbol}</span> <abbr className={`fn fn-${c.function}`} title={`${c.function} function`}>{FN_LABEL[c.function]}</abbr>
              </button>
            </li>
          ))}
        </ul>
        <p className="hint">Click a chord to hear it. T = tonic, PD = predominant, D = dominant (simplified functional grouping).</p>
      </section>
      <section aria-labelledby="th-now" aria-live="polite">
        <h3 id="th-now">Now playing</h3>
        {playing ? <p>{explainDegree(key, playing.degree, playing.seventh)}</p> : <p className="hint">Press Play to follow the chord progression here.</p>}
      </section>
      <section aria-labelledby="th-held" aria-live="polite">
        <h3 id="th-held">Notes you are holding</h3>
        {notes ? (
          <>
            <p className="notes">{notes.names.join(" ")}</p>
            <p>{notes.summary}</p>
            {notes.intervals.length > 0 && <p className="hint">Intervals: {notes.intervals.map((i) => `${i.from}→${i.to} ${i.name}`).join(", ")}.</p>}
          </>
        ) : <p className="hint">Hold notes on the keyboard to see their names, intervals and chord.</p>}
      </section>
    </aside>
  );
}
